import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { AdmissionsAPI } from "@/lib/sprint4-api";
import { InstitutionsAPI, ClassesAPI, SectionsAPI } from "@/lib/sprint2-api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, GraduationCap, ShieldCheck, Wallet, FileText, CheckCircle2, XCircle, MessageSquare, Receipt,
} from "lucide-react";

const STATUS_META = {
  applied:     { label: "Applied",      color: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20" },
  screening:   { label: "Screening",    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  shortlisted: { label: "Shortlisted",  color: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20" },
  admitted:    { label: "Admitted",     color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" },
  rejected:    { label: "Rejected",     color: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20" },
  withdrawn:   { label: "Withdrawn",    color: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border-zinc-500/20" },
};

const TRANSITIONS = {
  applied: ["screening", "shortlisted", "rejected", "withdrawn"],
  screening: ["shortlisted", "rejected", "withdrawn"],
  shortlisted: ["rejected", "withdrawn"], // 'admitted' uses /admit endpoint
  admitted: [],
  rejected: [],
  withdrawn: [],
};

export default function ApplicationDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [admitOpen, setAdmitOpen] = useState(false);
  const [feeOpen, setFeeOpen] = useState(false);
  const [noteText, setNoteText] = useState("");

  const appQuery = useQuery({
    queryKey: ["application", id],
    queryFn: () => AdmissionsAPI.get(id),
  });

  const institutionsQuery = useQuery({
    queryKey: ["institutions"],
    queryFn: () => InstitutionsAPI.list(),
    enabled: !!appQuery.data,
  });
  const classesQuery = useQuery({
    queryKey: ["classes-flat"],
    queryFn: () => ClassesAPI.list(),
    enabled: !!appQuery.data,
  });
  const sectionsQuery = useQuery({
    queryKey: ["sections", appQuery.data?.target_class_id],
    queryFn: () => SectionsAPI.list({ class_id: appQuery.data.target_class_id }),
    enabled: !!appQuery.data?.target_class_id,
  });

  if (appQuery.isLoading) {
    return <AppShell><div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div></AppShell>;
  }
  const a = appQuery.data;
  if (!a) {
    return <AppShell><div className="p-10 text-sm text-muted-foreground">Application not found.</div></AppShell>;
  }

  const inst = institutionsQuery.data?.find((i) => i.id === a.institution_id);
  const cls = classesQuery.data?.find((c) => c.id === a.target_class_id);
  const meta = STATUS_META[a.status] || STATUS_META.applied;
  const allowed = TRANSITIONS[a.status] || [];

  const transition = async (status, reason) => {
    try {
      await AdmissionsAPI.transition(id, status, reason);
      toast.success(`Moved to ${status}`);
      queryClient.invalidateQueries({ queryKey: ["application", id] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await AdmissionsAPI.note(id, noteText.trim());
      setNoteText("");
      toast.success("Note added");
      queryClient.invalidateQueries({ queryKey: ["application", id] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AppShell>
      <div className="p-8 max-w-5xl" data-testid="application-detail-page">
        <Link to="/admissions" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="h-3 w-3" /> Admissions
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-[0.25em] mb-2">
              /admissions/{id.slice(-8)}
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl flex items-center gap-3">
              {a.first_name} {a.last_name}
              <Badge className={meta.color + " border capitalize"} data-testid="app-status">{meta.label}</Badge>
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Applying to <b>{inst?.name || "—"}</b> · <b>{cls?.name || "—"}</b>
              {a.admission_no && <> · <span className="font-mono">Admission no. {a.admission_no}</span></>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {allowed.map((s) => (
              <Button
                key={s}
                size="sm"
                variant="outline"
                onClick={() => transition(s)}
                data-testid={`app-transition-${s}`}
                className="capitalize"
              >
                {s === "rejected" || s === "withdrawn" ? <XCircle className="h-3.5 w-3.5 mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                Move to {s}
              </Button>
            ))}
            {a.status === "shortlisted" && (
              <Button size="sm" onClick={() => setAdmitOpen(true)} data-testid="app-admit-button">
                <GraduationCap className="h-3.5 w-3.5 mr-1.5" /> Admit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: details */}
          <div className="lg:col-span-2 space-y-6">
            <Section title="Applicant">
              <Row k="Name">{a.first_name} {a.last_name}</Row>
              <Row k="Date of birth">{a.dob || "—"}</Row>
              <Row k="Gender" cap>{a.gender || "—"}</Row>
              <Row k="Previous school">{a.previous_school || "—"}</Row>
              <Row k="Blood group">{a.blood_group || "—"}</Row>
            </Section>

            <Section title="Guardian">
              <Row k="Name">{a.guardian_name}</Row>
              <Row k="Email" mono>{a.guardian_email}</Row>
              <Row k="Phone">{a.guardian_phone || "—"}</Row>
              <Row k="Relation" cap>{a.guardian_relation || "—"}</Row>
              <Row k="Address">{a.address || "—"}{a.city ? `, ${a.city}` : ""}</Row>
            </Section>

            <Section title="Notes" right={
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{a.notes.length}</span>
            }>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Internal note (visible to admissions staff only)…"
                    rows={2}
                    data-testid="app-note-text"
                  />
                  <Button onClick={addNote} disabled={!noteText.trim()} data-testid="app-note-add">
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {a.notes.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No notes yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {a.notes.slice().reverse().map((n, i) => (
                      <li key={i} className="border border-border rounded-md p-3 bg-secondary/30" data-testid={`app-note-${i}`}>
                        <div className="text-sm">{n.text}</div>
                        <div className="text-[10px] text-muted-foreground font-mono mt-1.5">
                          {n.by_email || n.by || "system"} · {n.at ? new Date(n.at).toLocaleString() : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Section>
          </div>

          {/* Right: workflow + fee */}
          <div className="space-y-6">
            <Section title="Workflow">
              <ol className="space-y-2 text-sm">
                {a.status_history.map((h, i) => {
                  const m = STATUS_META[h.status] || {};
                  return (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize">{m.label || h.status}</span>
                          <span className="text-[10px] font-mono text-muted-foreground">{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                        </div>
                        {h.reason && <div className="text-xs text-muted-foreground">{h.reason}</div>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </Section>

            <Section
              title="Admission fee"
              right={
                a.fee_paid
                  ? <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20 border">PAID</Badge>
                  : a.fee_required
                    ? <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20 border">DUE</Badge>
                    : <Badge variant="outline">NOT REQUIRED</Badge>
              }
            >
              {a.fee_required ? (
                <div className="space-y-1.5">
                  <Row k="Amount">{a.fee_currency} {a.fee_amount}</Row>
                  {a.fee_paid_at && <Row k="Paid at" mono>{new Date(a.fee_paid_at).toLocaleString()}</Row>}
                  {a.fee_txn_ref && <Row k="Txn ref" mono>{a.fee_txn_ref}</Row>}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Application fee collection is optional. Full Razorpay payment lands in Sprint 6.
                </p>
              )}
              {!a.fee_paid && (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => setFeeOpen(true)} data-testid="app-fee-button">
                  <Receipt className="h-3.5 w-3.5 mr-1.5" /> Mark fee paid
                </Button>
              )}
            </Section>

            {a.student_id && (
              <Section title="Linked student">
                <p className="text-sm">
                  Admitted as student <span className="font-mono">{a.admission_no}</span>.
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate("/students")}>
                  Open student roster
                </Button>
              </Section>
            )}
          </div>
        </div>

        {admitOpen && (
          <AdmitDialog
            application={a}
            sections={sectionsQuery.data || []}
            onClose={() => { setAdmitOpen(false); queryClient.invalidateQueries({ queryKey: ["application", id] }); }}
          />
        )}
        {feeOpen && (
          <FeeDialog
            applicationId={id}
            onClose={() => { setFeeOpen(false); queryClient.invalidateQueries({ queryKey: ["application", id] }); }}
          />
        )}
      </div>
    </AppShell>
  );
}

function Section({ title, right, children }) {
  return (
    <div className="border border-border rounded-md bg-card">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{title}</div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Row({ k, children, mono, cap }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold w-32 shrink-0">{k}</div>
      <div className={`text-sm ${mono ? "font-mono text-xs" : ""} ${cap ? "capitalize" : ""}`}>{children}</div>
    </div>
  );
}

function AdmitDialog({ application, sections, onClose }) {
  const [admissionNo, setAdmissionNo] = useState("");
  const [sectionId, setSectionId] = useState(application.target_section_id || "");
  const [requireFee, setRequireFee] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      await AdmissionsAPI.admit(application.id, {
        admission_no: admissionNo,
        target_section_id: sectionId || undefined,
        require_fee_paid: requireFee,
      });
      toast.success(`Admitted as student ${admissionNo}`);
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admit-dialog">
        <DialogHeader>
          <DialogTitle>Admit {application.first_name} {application.last_name}</DialogTitle>
          <DialogDescription>Creates a Student record linked to this application.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Admission no.</Label>
            <Input
              value={admissionNo}
              onChange={(e) => setAdmissionNo(e.target.value)}
              placeholder="ADM-2025-001"
              className="font-mono"
              data-testid="admit-admission-no"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Section</Label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger data-testid="admit-section"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · cap {s.capacity}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={requireFee} onChange={(e) => setRequireFee(e.target.checked)} data-testid="admit-require-fee" />
            Require admission fee paid before admitting
          </label>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !admissionNo.trim()} data-testid="admit-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Admit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeeDialog({ applicationId, onClose }) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [txnRef, setTxnRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      await AdmissionsAPI.markFeePaid(applicationId, { amount: Number(amount) || 0, currency, txn_ref: txnRef || undefined });
      toast.success("Fee marked as paid");
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark admission fee as paid</DialogTitle>
          <DialogDescription>Manual record. Razorpay collection arrives in Sprint 6.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500" data-testid="fee-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Currency</Label>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value)} className="font-mono" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Transaction ref</Label>
            <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder="Optional" className="font-mono" data-testid="fee-txn-ref" />
          </div>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !amount} data-testid="fee-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Mark paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
